import { useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import {
	TranscriptionLanguageSelect,
	localeToTranscriptionLanguage,
} from 'src/Recording/TranscriptionLanguageSelect';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks/src/useFeature';
import { useLanguage } from '@treams/treams-hooks/src/useLanguage';
import { useLocalize } from '@treams/treams-hooks/src/useLocalize';
import * as graphql from '@treams/treams-network';
import { IconButton } from '@treams/treams-ui/common/button/IconButton';
import { Callout } from '@treams/treams-ui/common/Callout';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { IconWrapper } from '@treams/treams-ui/common/Icon';
import { NumberedSubheading } from '@treams/treams-ui/common/NumberedSubheading';
import { Radio } from '@treams/treams-ui/common/Radio';
import { Heading, Label, Paragraph, Subheading } from '@treams/treams-ui/common/Text';
import { Tooltip } from '@treams/treams-ui/common/Tooltip';
import { Checkbox } from '@treams/treams-ui/form/controls/Checkbox';
import { QuillInput } from '@treams/treams-ui/form/controls/QuillInput';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';
import { StarRating, StarRatingElement } from '@treams/treams-ui/form/controls/StarRating';
import {
	CheckmarkIcon,
	HelpIcon,
	MicrophoneIcon,
	PlaybackStopIcon,
	VisibilityOffIcon,
} from '@treams/treams-ui/icons';

import VoiceMemoPlayback from '../../../../../../packages/@treams/treams-app/src/components/voice-memos/VoiceMemoPlayback';
import VoiceMemoTarget from '../../../../../../packages/@treams/treams-app/src/components/voice-memos/VoiceMemoTarget';
import { FeedbackQuestionMoreInformation } from '../../../../../feedback/FeedbackQuestionMoreInformation';
import { feedbackQuestionLevels } from '../../../../../feedback/feedbackQuestionLevels';
import { FeedbackSuggestionsAI } from '../../../../../feedback/results/FeedbackSuggestionsAI';
import GoalCard from '../../../../../goals/GoalCard';
import CompetencyLevelsInput from '../../../common/containers/CompetencyInput';
import FeedbackComposerGoalCard from '../composer/FeedbackComposerGoalCard';
import { useFeedbackVoiceStream } from './useFeedbackVoiceStream';

const Answer = styled(Box)`
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
	background: ${(props) => props.theme.tokens.surface.secondary};
`;

const StyledTooltip = styled(Tooltip)`
	&:hover {
		text-decoration: underline;
		cursor: pointer;
	}
`;

const HiddenFromSubjectNote = styled(Flex)`
	align-items: center;
	color: ${(props) => props.theme.tokens.text.secondary};
`;

const NotObserved = styled(Flex)`
	color: ${(props) => props.theme.tokens.info.text};

	> svg {
		width: 18px;
		height: 18px;
		color: ${(props) => props.theme.tokens.info.icon};
		path {
			fill: currentColor;
		}
	}
`;

export default function FeedbackResponseAnswer(props: {
	question: graphql.FeedbackRequestQuestion;
	answer?: graphql.FeedbackResponseAnswer;
	error?: boolean;
	name: string;
	index: number;
	viewerCanUpdate: boolean;
	responseID?: string;
	/**
	 * Magic-link respondents keep the voice memo. Transcription is only for
	 * someone signed in to the workspace.
	 */
	voiceTranscription?: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const hasVoiceMemos = useFeature(graphql.FeatureName.FeedbackVoiceMemos);
	const voiceTranscriptionFeature = useFeature(graphql.FeatureName.FeedbackVoiceMemoTranscription);
	const hasVoiceTranscription = props.voiceTranscription !== false && voiceTranscriptionFeature;
	const hasCompetencyNotObservedOption = useFeature(
		graphql.FeatureName.CompetencyFeedbackNotObservedOption,
	);
	const jobStatement = props.question.jobCompetency?.description
		? localize(props.question.jobCompetency.description).text
		: undefined;

	const [{ value: text }, , { setValue: setText }] = useField(`${props.name}.text`);
	const [{ value: voiceMemo }, , { setValue: setVoiceMemo }] = useField(`${props.name}.voiceMemo`);
	const [, { value: competency }, { setValue: setCompetency }] = useField(
		`${props.name}.competencyLevelIndex`,
	);
	const [{ value: selectedOptionID }, , { setValue: setSelectedOptionID }] = useField(
		`${props.name}.selectedOptionID`,
	);

	const isNotObserved = competency === null;
	const optionsSideBySide = props.question.options?.length === 2;

	return (
		<Box pt={2} pb={3}>
			<NumberedSubheading index={props.index + 1}>
				<Heading>
					{props.question.localizedText
						? localize(props.question.localizedText).text
						: props.question.text}
				</Heading>
				{props.question.subTitle && <Paragraph mt={2}>{props.question.subTitle}</Paragraph>}
				{!QuillText.isEmpty(props.question.additionalDetails) && (
					<FeedbackQuestionMoreInformation additionalDetails={props.question.additionalDetails} />
				)}
			</NumberedSubheading>
			<Flex mt={3}>
				<Box flexGrow={1} pl="42px">
					{props.question.competency && (
						<Box mb={3}>
							<FlexGap
								gap={2}
								mb={hasCompetencyNotObservedOption ? 2 : 0}
								justifyContent="space-between"
								alignItems="center"
							>
								{props.question.competency.description ? (
									<Tooltip
										content={
											<QuillText value={localize(props.question.competency.description).text} />
										}
									>
										<Flex mb={2} alignItems="center" justifyContent="center">
											<Subheading>{localize(props.question.competency.title).text}</Subheading>
											<IconWrapper ml={1} size={16}>
												<HelpIcon />
											</IconWrapper>
										</Flex>
									</Tooltip>
								) : (
									<Subheading mb={2}>{localize(props.question.competency.title).text}</Subheading>
								)}
								{hasCompetencyNotObservedOption && (
									<StyledTooltip
										content={
											<FlexGap gap={4} flexDirection="column" alignItems="center">
												{t('feedback.answer.not.observed.explanation')}
												<Checkbox
													checked={isNotObserved}
													label={t('feedback.answer.not.observed')}
													onClick={() =>
														!isNotObserved ? setCompetency(null) : setCompetency(undefined)
													}
												/>
											</FlexGap>
										}
									>
										{isNotObserved ? (
											<NotObserved>
												<CheckmarkIcon />
												{t('feedback.answer.not.observed')}
											</NotObserved>
										) : (
											t('feedback.answer.not.observed.question')
										)}
									</StyledTooltip>
								)}
							</FlexGap>
							{!QuillText.isEmpty(jobStatement) && (
								<Box mb={2}>
									<QuillText value={jobStatement} />
								</Box>
							)}
							{(competency !== null || !hasCompetencyNotObservedOption) && (
								<CompetencyLevelsInput
									party={graphql.EvaluationSubmissionParty.Manager}
									competency={{
										...props.question.competency,
										levels: feedbackQuestionLevels(props.question),
									}}
									name={`${props.name}.competencyLevelIndex`}
									suffix=""
								/>
							)}
						</Box>
					)}
					{props.question.goal && (
						<Box mb={3}>
							<FeedbackComposerGoalCard goal={props.question.goal} />
						</Box>
					)}
					{props.question.goalRevision && (
						<Box mb={3}>
							<GoalCard goal={props.question.goalRevision} modal />
						</Box>
					)}
					{props.error && (
						<Callout color="red" mb={3}>
							{t('feedback.answer.required')}
						</Callout>
					)}
					{!(props.question.canSubjectView ?? true) && (
						<HiddenFromSubjectNote mb={2}>
							<IconWrapper mr={1} size={16}>
								<VisibilityOffIcon />
							</IconWrapper>
							<Paragraph>{t('feedback.answer.hiddenFromEmployee.note')}</Paragraph>
						</HiddenFromSubjectNote>
					)}
					{(props.question.type === graphql.FeedbackQuestionType.Stars ||
						props.question.type === graphql.FeedbackQuestionType.Both) && (
						<Box pb={3}>
							{props.viewerCanUpdate && (
								<Label mb={2}>{t('feedback.answer.howDidUserPerform')}</Label>
							)}
							{props.viewerCanUpdate ? (
								<StarRating name={`${props.name}.stars`} />
							) : (
								<StarRatingElement
									value={props.answer?.stars ?? 0}
									name={`${props.name}.stars`}
									disabled
								/>
							)}
						</Box>
					)}

					{props.question.type === graphql.FeedbackQuestionType.MultipleChoice && (
						<Box pb={3}>
							<FlexGap gap={8} flexDirection={optionsSideBySide ? 'row' : 'column'}>
								{props.question.options?.map((option) => {
									const selected =
										(props.viewerCanUpdate ? selectedOptionID : props.answer?.selectedOptionID) ===
										option.id;

									return (
										<Box
											key={option.id}
											flexGrow={0}
											flexShrink={0}
											style={{
												...(optionsSideBySide
													? {
															flexBasis: 'calc((100% - 16px) / 2)',
															maxWidth: 'calc((100% - 16px) / 2)',
														}
													: undefined),
												...(props.viewerCanUpdate ? undefined : { pointerEvents: 'none' }),
											}}
										>
											<Radio.Card
												checked={selected}
												title={localize(option.text).text}
												onChange={() => props.viewerCanUpdate && setSelectedOptionID(option.id)}
											/>
										</Box>
									);
								})}
							</FlexGap>
						</Box>
					)}

					{(props.question.type === graphql.FeedbackQuestionType.Text ||
						props.question.type === graphql.FeedbackQuestionType.Both) && (
						<Box pb={3}>
							{props.viewerCanUpdate &&
								!props.question.subTitle &&
								(props.question.canSubjectView ?? true) && (
									<Label mb={2}>
										{props.question.competency
											? t('feedback.answer.explainOptional')
											: t('feedback.answer.explain')}
									</Label>
								)}
							{props.viewerCanUpdate ? (
								<FeedbackTextInput
									hasVoiceMemos={hasVoiceMemos}
									hasVoiceTranscription={hasVoiceTranscription}
									voiceMemo={voiceMemo}
									setVoiceMemo={setVoiceMemo}
									setText={setText}
									hasAI={hasAI}
									question={props.question}
									name={props.name}
									text={text}
									responseID={props.responseID}
									answerLabel={
										props.question.competency
											? t('feedback.answer.explainOptional')
											: t('feedback.answer.explain')
									}
								/>
							) : (
								<Answer p={3}>
									<QuillText
										value={props.answer?.text}
										placeholder={t('feedback.answer.emptyTextQuestionPlaceholder')}
									/>
								</Answer>
							)}
						</Box>
					)}
				</Box>
			</Flex>
		</Box>
	);
}

function FeedbackTextInput(props: {
	hasVoiceMemos: boolean;
	hasVoiceTranscription: boolean;
	voiceMemo: any;
	setVoiceMemo: (value: any) => void;
	setText: (value: any) => void;
	hasAI: boolean;
	question: graphql.FeedbackRequestQuestion;
	name: string;
	text: any;
	responseID?: string;
	answerLabel: string;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const { id: locale } = useLanguage();
	const [language, setLanguage] = useState(() => localeToTranscriptionLanguage(locale));
	const [failed, setFailed] = useState(false);
	const transcribe = props.hasVoiceMemos && props.hasVoiceTranscription;
	const stream = useFeedbackVoiceStream({
		language,
		responseID: props.responseID,
		text: props.text,
		onText: props.setText,
		onError: () => setFailed(true),
	});
	const recordingRef = useRef(stream.recording);
	recordingRef.current = stream.recording;

	useEffect(() => {
		if (!recordingRef.current) {
			setLanguage(localeToTranscriptionLanguage(locale));
		}
	}, [locale]);

	const textInput = props.hasAI ? (
		<FeedbackSuggestionsAI
			name="feedback.answer.suggestImprovements"
			context={{
				question: props.question.localizedText
					? localize(props.question.localizedText).text
					: props.question.text,
				answer: props.text?.ops.map((op: any) => op.insert ?? '').join('') ?? '',
			}}
			tooltip={t('feedback.answer.ai.button')}
			offsetTop={props.hasVoiceMemos && !transcribe ? 40 : 0}
		>
			<QuillInput.Formik
				name={`${props.name}.text`}
				label={props.answerLabel}
				readOnly={stream.partial.length > 0}
				applyExternalValue={transcribe}
			/>
		</FeedbackSuggestionsAI>
	) : (
		<QuillInput.Formik
			name={`${props.name}.text`}
			label={props.answerLabel}
			readOnly={stream.partial.length > 0}
			applyExternalValue={transcribe}
		/>
	);

	if (transcribe) {
		return (
			<FlexGap gap={2} flexDirection="column">
				<FlexGap gap={2} alignItems="center">
					<TranscriptionLanguageSelect
						value={language}
						aria-label={t('voiceMemo.transcription.language')}
						onChange={(next) => {
							if (!stream.recording) {
								setLanguage(next);
							}
						}}
					/>
					{stream.recording ? (
						<IconButton
							type="destructive"
							icon={<PlaybackStopIcon />}
							aria-label={t('voiceMemo.transcription.stop')}
							onClick={() => stream.stop()}
						/>
					) : (
						<IconButton
							type="destructive"
							icon={<MicrophoneIcon />}
							aria-label={t('voiceMemo.record.tooltip')}
							onClick={() => {
								setFailed(false);
								void stream.start();
							}}
						/>
					)}
				</FlexGap>
				{props.voiceMemo?.asset && (
					<Box role="group" aria-label={t('voiceMemo.playback')}>
						<VoiceMemoPlayback
							source={props.voiceMemo.asset.source.url}
							samples={props.voiceMemo.samples}
						/>
					</Box>
				)}
				{failed && (
					<Box role="alert" aria-label={t('voiceMemo.transcription.error')}>
						<Callout color="danger">{t('voiceMemo.transcription.error')}</Callout>
					</Box>
				)}
				{textInput}
			</FlexGap>
		);
	}

	if (!props.hasVoiceMemos) {
		return textInput;
	}

	return (
		<VoiceMemoTarget voiceMemo={props.voiceMemo} onChange={props.setVoiceMemo}>
			{textInput}
		</VoiceMemoTarget>
	);
}
